// ─────────────────────────────────────────────────────────────────────────────
// Messages — the ONE behaviour layer behind both surfaces (DESIGN-CONCEPT-MOBILE
// §1.1). `Messages.web.jsx` and `Messages.mobile.jsx` share nothing visual and
// disagree about nothing else: what the rows are, which one is selected, what
// the thread holds, whether a send is in flight — all of it is here.
//
// Deliberately DOM-free and component-free: no refs, no elements, no measuring.
// It composes three sources:
//
//   1. the BFF directory  — `chatConversations` (who each room is with) and
//      `chatContacts` (who a conversation could be started with). Fetched with
//      the loading/error/nonce shape `useEncounters` established.
//   2. the matrix runtime — live rooms, unread counts and the open room's
//      timeline, read through `chatRuntime`'s external store.
//   3. the URL           — selection (`?c=`) and the unread
//      filter (`?unread=1`). §3.2: the URL IS the state; there is no component
//      state for any of the three, so refresh / back / a shared link restore the
//      exact view. `roomId` is opaque, which is what makes it safe to put there.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePhiSearchTerm } from '../../../lib/phiSearch';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import {
  ensureChatStarted, getChatClient, getChatRevision, subscribeChat,
  getCanSend, getReadOnlyReason,
} from '../../../lib/chatRuntime';
import {
  mergeConversations, contactStubs, matchesSearch, filterUnread,
} from '../Messages.shared';

export const CHAT_DIRECTORY_QUERY = `
  query ChatDirectory {
    chatConversations(limit: 200) {
      conversations {
        roomId
        peerMxid
        peerType
        peerSubjectId
        peerDisplayName
        createdAt
      }
      totalCount
    }
    chatContacts(limit: 200) {
      contacts {
        subjectType
        subjectId
        displayName
        role
        mxid
        roomId
        reachable
      }
      totalCount
    }
  }
`;

const OPEN_CONVERSATION_MUTATION = `
  mutation OpenChatConversation($subjectType: String!, $subjectId: ID!) {
    openChatConversation(subjectType: $subjectType, subjectId: $subjectId) {
      roomId
      peerType
      peerSubjectId
      peerDisplayName
      created
      createdAt
    }
  }
`;

// The URL carries only what is NOT at its default; these are the defaults.
const DEFAULTS = { c: '', q: '', unread: '' };

const EMPTY_DIRECTORY = { conversations: [], contacts: [] };

export function useMessages() {
  const [searchParams, setSearchParams] = useSearchParams();

  const param = (k) => searchParams.get(k) ?? DEFAULTS[k];
  const selectedRoomId = param('c') || null;
  // The search term is a person's name — kept OUT of the URL (§3.2), in the
  // tab's session instead (lib/phiSearch). An old `?q=` link is migrated.
  const [query, setQuery] = usePhiSearchTerm('messages');
  const unreadOnly = param('unread') === '1';

  // A param at its default is REMOVED. Whether the write PUSHES or REPLACES is
  // the caller's decision, because the two kinds of param are not the same kind
  // of fact — see `setFilterParams` below.
  const setParams = useCallback((patch, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (k === 'q') continue;
      if (v == null || v === '' || v === DEFAULTS[k]) next.delete(k);
      else next.set(k, v);
    }
    next.delete('q');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  // Filter churn REPLACES: typing a search must not bury the back button under
  // twelve entries. Selection PUSHES (plain `setParams`): on the phone `?c=` is
  // the boundary between two SCREENS, so hardware Back has to return
  // thread → list rather than leaving /messages altogether.
  const setFilterParams = useCallback(
    (patch) => setParams(patch, { replace: true }),
    [setParams],
  );

  // ── 1. The BFF directory ────────────────────────────────────────────────────
  const [directory, setDirectory] = useState(EMPTY_DIRECTORY);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // TWO nonces, deliberately. One shared counter meant the thread's «تلاش
  // مجدد» — wired to a dead homeserver session — also re-ran the directory
  // fetch and blanked twelve perfectly good rows into skeletons. One region's
  // error never blanks another region (§2.4).
  const [directoryNonce, setDirectoryNonce] = useState(0);
  const [chatNonce, setChatNonce] = useState(0);
  const [loadedOnce, setLoadedOnce] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // The FIRST load is a skeleton; every later one keeps the rows on screen
    // and merely dims them — a refetch is not a reason to take content away.
    if (loadedOnce) setRefreshing(true);
    else setLoading(true);
    setError(null);
    bffQuery(CHAT_DIRECTORY_QUERY)
      .then((data) => {
        if (cancelled) return;
        setDirectory({
          conversations: data?.chatConversations?.conversations || [],
          contacts: data?.chatContacts?.contacts || [],
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('chat directory failed:', err);
        setError(err);
        setDirectory(EMPTY_DIRECTORY);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
        setLoadedOnce(true);
      });
    return () => { cancelled = true; };
    // `loadedOnce` is read, never depended on: it must not re-run the fetch.
    // The effect closure is rebuilt every render, so the run that a nonce bump
    // schedules already carries the current value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directoryNonce]);

  /** Re-read the BFF directory (the LIST). Also what a new room needs. */
  const reload = useCallback(() => setDirectoryNonce((n) => n + 1), []);

  // ── 2. The matrix runtime ───────────────────────────────────────────────────
  // Bootstrapping is separate from the directory: the list is perfectly usable
  // while the homeserver session is still being minted, and a failed session
  // must take down the THREAD only — never the list (§2.4).
  const [chatError, setChatError] = useState(null);
  const [chatReady, setChatReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setChatError(null);
    ensureChatStarted()
      .then(() => { if (!cancelled) setChatReady(true); })
      .catch((err) => {
        if (cancelled) return;
        console.error('chat session failed:', err);
        setChatReady(false);
        setChatError(err);
      });
    return () => { cancelled = true; };
  }, [chatNonce]);

  /** Restart the homeserver session (the THREAD). Touches no BFF data. */
  const reloadThread = useCallback(() => setChatNonce((n) => n + 1), []);

  // The store snapshot is an integer (see chatRuntime): the room/timeline arrays
  // are derived from it, so React compares a number and not a fresh array.
  const revision = useSyncExternalStore(subscribeChat, getChatRevision, () => 0);
  const client = getChatClient();

  // `revision` is the dependency that matters: the client object is stable for
  // the whole session, so without it these would never re-read.
  const rooms = useMemo(
    () => (client ? client.getRooms() : []),
    [client, revision], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const timeline = useMemo(
    () => (client && selectedRoomId ? client.getTimeline(selectedRoomId) : []),
    [client, selectedRoomId, revision], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── 3. Rows ─────────────────────────────────────────────────────────────────
  const conversations = useMemo(
    () => mergeConversations(directory.conversations, rooms),
    [directory.conversations, rooms],
  );

  const rows = useMemo(() => {
    const searched = conversations.filter((row) => matchesSearch(row, query));
    const filtered = unreadOnly ? filterUnread(searched) : searched;
    // Contacts with no room yet are a SEARCH result, never part of the resting
    // list — and never part of an unread-filtered one: a conversation that does
    // not exist cannot be unread.
    if (unreadOnly) return filtered;
    return [...filtered, ...contactStubs(directory.contacts, conversations, query)];
  }, [conversations, directory.contacts, query, unreadOnly]);

  // Selection is resolved against the UNFILTERED set: narrowing the list must
  // not close the thread the doctor is reading.
  const selected = useMemo(
    () => conversations.find((row) => row.roomId === selectedRoomId) || null,
    [conversations, selectedRoomId],
  );

  const hasActiveFilters = query !== DEFAULTS.q || unreadOnly;

  // ── Selection / filters ─────────────────────────────────────────────────────
  const select = useCallback((roomId) => setParams({ c: roomId || '' }), [setParams]);
  const clearSelection = useCallback(() => setParams({ c: '' }), [setParams]);
  const setSearch = useCallback((value) => setQuery(value), [setQuery]);
  const setUnreadOnly = useCallback((on) => setFilterParams({ unread: on ? '1' : '' }), [setFilterParams]);
  const clearFilters = useCallback(() => { setFilterParams({ unread: '' }); setQuery(''); }, [setQuery, setFilterParams]);

  // ── Opening a conversation ──────────────────────────────────────────────────
  // A row that already has a room just becomes the selection. A contact stub
  // has to be given one first — that mutation is the only write the list makes.
  const [opening, setOpening] = useState(false);
  const openConversation = useCallback(async (row) => {
    if (!row) return null;
    if (row.roomId) { select(row.roomId); return row.roomId; }
    setOpening(true);
    try {
      const data = await bffMutation(OPEN_CONVERSATION_MUTATION, {
        subjectType: row.peerType,
        subjectId: row.peerSubjectId,
      });
      const roomId = data?.openChatConversation?.roomId;
      if (!roomId) throw new Error('openChatConversation: no roomId');
      // Re-read the directory so the new room arrives with its peer identity
      // attached; selecting it before that would render an unknown contact.
      reload();
      select(roomId);
      return roomId;
    } finally {
      setOpening(false);
    }
  }, [select, reload]);

  // ── Reading ─────────────────────────────────────────────────────────────────
  // Mark read when the thread OPENS and again whenever a newer real event lands
  // while it is open. Keyed on the last event id rather than the raw revision so
  // a local echo or an unrelated room's sync does not re-post a receipt.
  const lastEventId = timeline.length ? timeline[timeline.length - 1].eventId : null;
  useEffect(() => {
    if (!selectedRoomId) return;
    getChatClient()?.markRead(selectedRoomId);
  }, [selectedRoomId, lastEventId]);

  // ── Writing ─────────────────────────────────────────────────────────────────
  const [sending, setSending] = useState(false);

  /**
   * Send, and report honestly whether it landed. matrix-lite never rejects — a
   * failed write flips its own entry to `failed` and waits — so the outcome is
   * read back off the timeline. Returns false on failure; the caller keeps the
   * draft and surfaces the error. Nothing here retries.
   */
  const send = useCallback(async (body) => {
    const c = getChatClient();
    const text = (body || '').trim();
    if (!c || !selectedRoomId || !text) return false;
    setSending(true);
    try {
      const txnId = await c.sendMessage(selectedRoomId, text);
      const entry = c.getTimeline(selectedRoomId).find((e) => e.txnId === txnId);
      if (entry && entry.failed) return false;
      await c.markRead(selectedRoomId);
      return true;
    } finally {
      setSending(false);
    }
  }, [selectedRoomId]);

  /** Re-send one failed bubble. Caller-invoked only (ChatBubble's onRetry). */
  const retry = useCallback((txnId) => {
    if (!selectedRoomId) return;
    getChatClient()?.retryMessage(selectedRoomId, txnId);
  }, [selectedRoomId]);

  // ── Backwards pagination ────────────────────────────────────────────────────
  // Never automatic (the pagination-not-infinite-scroll rule): ChatThread's
  // expander calls this. `hasOlder` starts true for a synced room and latches
  // false once the server says there is nothing before the current page.
  const [olderState, setOlderState] = useState({});
  const older = olderState[selectedRoomId] || { loading: false, exhausted: false };

  const loadOlder = useCallback(async () => {
    const c = getChatClient();
    if (!c || !selectedRoomId) return;
    // Re-entrancy guard (the patient app's `useThread` has the same one): the
    // kit disables the expander while a page is in flight, so this is not
    // reachable by clicking — but a second call would set `loading` twice and
    // let the slower page's `exhausted` overwrite the faster one's.
    if (older.loading) return;
    setOlderState((s) => ({ ...s, [selectedRoomId]: { loading: true, exhausted: false } }));
    try {
      const { hasMore } = await c.loadOlder(selectedRoomId);
      setOlderState((s) => ({ ...s, [selectedRoomId]: { loading: false, exhausted: !hasMore } }));
    } catch {
      // A failed page is not the start of the room — keep the expander offered.
      setOlderState((s) => ({ ...s, [selectedRoomId]: { loading: false, exhausted: false } }));
    }
  }, [selectedRoomId, older.loading]);

  return {
    // list
    rows, loading, refreshing, error, reload,
    query, unreadOnly, hasActiveFilters,
    setSearch, setUnreadOnly, clearFilters,
    // selection
    selectedRoomId, selected, select, clearSelection,
    openConversation, opening,
    // thread
    timeline, chatReady, chatError, reloadThread,
    hasOlder: Boolean(selectedRoomId) && !older.exhausted && timeline.length > 0,
    loadingOlder: older.loading,
    loadOlder,
    // composing
    send, sending, retry,
    canSend: getCanSend(),
    readOnlyReason: getReadOnlyReason(),
  };
}

export default useMessages;
